import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button, Spin, Alert, message } from "antd";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import {
  getTenantIntentContext,
  postTenantIntentNotice,
  postTenantIntentRenewal,
} from "../../lib/api/tenantIntentApi";
import { APP_NAME } from "../../helpers/constants";
import { device } from "../../lib/breakpoints";

const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  min-height: -webkit-fill-available;
  background: linear-gradient(180deg, #f8faf8 0%, #f0f4f1 100%);
  padding: calc(1rem + env(safe-area-inset-top, 0px))
    calc(1rem + env(safe-area-inset-right, 0px))
    calc(1.25rem + env(safe-area-inset-bottom, 0px))
    calc(1rem + env(safe-area-inset-left, 0px));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.08);
  padding: 1.5rem 1.25rem 1.75rem;
  margin-top: 0.5rem;
  box-sizing: border-box;

  @media ${device.mobile} {
    border-radius: 20px;
    padding-bottom: calc(1.75rem + env(safe-area-inset-bottom, 0px));
  }
`;

const Title = styled.h1`
  font-size: 1.35rem;
  font-weight: 600;
  color: #1a2e1b;
  margin: 0 0 0.35rem;
  line-height: 1.3;
`;

const Sub = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: #5c6b5e;
  line-height: 1.45;
`;

const Brand = styled.div`
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #2c5f2d;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.25rem;
`;

const TenantIntentPage = () => {
  const { token: rawToken } = useParams();
  const token = rawToken ? decodeURIComponent(rawToken) : "";
  const [renewalDone, setRenewalDone] = useState(false);
  const [noticeDone, setNoticeDone] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["tenant-intent", token],
    queryFn: () => getTenantIntentContext(token),
    enabled: !!token,
  });

  const renewalMutation = useMutation({
    mutationFn: () => postTenantIntentRenewal(token),
    onSuccess: () => {
      setRenewalDone(true);
      message.success("Your landlord has been notified.");
    },
    onError: (e) => message.error(e.message || "Something went wrong"),
  });

  const noticeMutation = useMutation({
    mutationFn: () => postTenantIntentNotice(token),
    onSuccess: () => {
      setNoticeDone(true);
      message.success("Notice recorded.");
    },
    onError: (e) => message.error(e.message || "Something went wrong"),
  });

  const endLine = data?.leaseEndLabel
    ? `Current lease end on file: ${data.leaseEndLabel}.`
    : null;

  return (
    <Page>
      <Helmet>
        <title>Tenant intent · {APP_NAME}</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
      </Helmet>
      <Card>
        <Brand>{APP_NAME}</Brand>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <Spin size="large" />
          </div>
        ) : isError ? (
          <Alert
            type="error"
            message="Link not valid"
            description={error?.message || "Ask your landlord for a new link."}
            showIcon
          />
        ) : (
          <>
            <Title>What would you like to do?</Title>
            <Sub>
              {data?.unitSummary || "Your lease"}
              {endLine ? ` ${endLine}` : ""} Choose one option below. Your
              landlord will be notified.
            </Sub>
            {renewalDone ? (
              <Alert
                type="success"
                showIcon
                message="Renewal request sent"
                description="Your landlord has been emailed. They may follow up with you directly."
                style={{ marginBottom: 12 }}
              />
            ) : null}
            {noticeDone ? (
              <Alert
                type="info"
                showIcon
                message="Notice recorded"
                description="Your lease is marked Ending in LandlordOps and your landlord has been notified."
                style={{ marginBottom: 12 }}
              />
            ) : null}
            <Actions>
              <Button
                type="primary"
                size="large"
                block
                loading={renewalMutation.isPending}
                disabled={renewalDone || noticeDone}
                onClick={() => renewalMutation.mutate()}
                style={{ height: 52, touchAction: "manipulation" }}
              >
                Request renewal
              </Button>
              <Button
                danger
                size="large"
                block
                loading={noticeMutation.isPending}
                disabled={noticeDone}
                onClick={() => noticeMutation.mutate()}
                style={{ height: 52, touchAction: "manipulation" }}
              >
                Give notice
              </Button>
            </Actions>
          </>
        )}
      </Card>
    </Page>
  );
};

export default TenantIntentPage;
